'use client'

import { getColumnWidths, getTableElement } from './helpers/resize-helpers'
import { Column, TableHeaderVariants, TableVariants, TableSizes, ColumnInformation } from '../../types'
import React, { createContext, useContext, useEffect, useId, useState, useMemo } from 'react'

export interface TableProviderModel {
    columns: Column[]
    variant: TableVariants
    size: TableSizes
    tableId?: string
    columnWidths?: ColumnInformation[]
    isCollapsible: boolean
    isEditable: boolean
    isInitialized: boolean
    headerVariant?: TableHeaderVariants
    isInline?: boolean
    isStretched?: boolean
    isScrollable: boolean
    isNested?: boolean
    sortHandler?: ((sortBy: string) => void) | undefined
    hideHeader?: boolean
    hasBorder?: boolean
    hasBackground?: boolean
    isLoading?: boolean
}

export const TableContext = createContext<TableProviderModel>({
    columns: [],
    variant: TableVariants.STRETCHED,
    headerVariant: TableHeaderVariants.REGULAR,
    size: TableSizes.MEDIUM,
    isCollapsible: false,
    isEditable: false,
    isInitialized: false,
    isScrollable: false,
    hideHeader: false,
})

/** @deprecated use useContext(TableContext) */
export const useTableContext = () => {
    return useContext(TableContext)
}

interface TableProviderProps
    extends Pick<
        TableProviderModel,
        | 'columns'
        | 'variant'
        | 'isCollapsible'
        | 'isEditable'
        | 'sortHandler'
        | 'hideHeader'
        | 'hasBorder'
        | 'hasBackground'
        | 'headerVariant'
        | 'isLoading'
    > {
    size: TableSizes | undefined
    children: React.ReactNode
}

export const TableProvider = ({
    columns = [],
    variant,
    size,
    isCollapsible = false,
    isEditable = false,
    sortHandler,
    hideHeader,
    hasBorder,
    hasBackground,
    headerVariant,
    isLoading,
    children,
}: TableProviderProps) => {
    const tableId = useId().replace(/:/g, 'table-id')
    const parentProvider = useContext(TableContext)
    const [columnWidths, setColumnWidths] = useState<ColumnInformation[]>()
    const hasStickyColumns = () => columns.some((col) => col.sticky)

    useEffect(() => {
        if (tableId) setColumnWidths(getColumnWidths(tableId))
    }, [tableId])

    const resizeObserver = hasStickyColumns()
        ? new ResizeObserver(() => setColumnWidths(getColumnWidths(tableId)))
        : null

    const getAllColumns = (): Column[] => {
        const allColumns = columns
        if (isCollapsible && !allColumns.find((column) => column.isCollapseColumn)) {
            allColumns.push({
                title: '',
                hasNoPadding: true,
                isActionColumn: true,
                isCollapseColumn: true,
                sticky: allColumns.at(-1)?.sticky === 'right' ? 'right' : undefined,
            })
        }
        return allColumns.map((column, index) => ({ id: index, ...column }))
    }

    /**
     * Set up resize observer for every table header cell
     * Their width can change individually from getting fresh data, and we want to recalculate all their positions based on those widths.
     */
    const setUpEventListeners = () => {
        const table = getTableElement(tableId)
        if (!table) return

        table
            .querySelectorAll('.table-header-cell--sticky-right')
            .forEach((headerCellElement) => resizeObserver?.observe(headerCellElement))
    }

    useEffect(() => {
        if (hasStickyColumns()) setUpEventListeners()
        return () => resizeObserver?.disconnect()
    }, [])

    const isNested: boolean = parentProvider?.isInitialized
    const isStretched: boolean = variant === TableVariants.STRETCHED && !isNested
    const defaultSize: TableSizes = isStretched ? TableSizes.LARGE : TableSizes.MEDIUM

    const providerValue: TableProviderModel = useMemo(
        () => ({
            columns: getAllColumns(),
            variant,
            size: size ?? defaultSize,
            isCollapsible,
            isEditable,
            isInline: variant === TableVariants.INLINE,
            isStretched,
            isNested,
            hideHeader,
            hasBorder,
            hasBackground,
            isScrollable: isStretched,
            isInitialized: true,
            headerVariant,
            ...(sortHandler ? { sortHandler } : {}),
            isLoading,
            columnWidths,
            tableId,
        }),
        [
            columns,
            variant,
            size,
            defaultSize,
            isCollapsible,
            isEditable,
            isStretched,
            isNested,
            hideHeader,
            hasBorder,
            hasBackground,
            headerVariant,
            sortHandler,
            isLoading,
            columnWidths,
            tableId,
        ],
    )

    return <TableContext.Provider value={providerValue}>{children}</TableContext.Provider>
}
